import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../contexts/I18nContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { Map, Plus, MapPin, Trash2, Navigation, Clock, Building2 } from "lucide-react";
import { toast } from "sonner";
import RowActions from "../../components/RowActions";
import { useSchools, SchoolSelect } from "../../components/SchoolSelect";
import { localName, localFullName } from "../../lib/localName";

const EMPTY_STOP = { name: "", address: "", lat: "", lng: "", eta_offset_min: 0, order: 0 };
const EMPTY = { name: "", direction: "morning", driver_id: "", vehicle_id: "", start_time: "06:30", stops: [], active: true, notes: "" };

function googleMapsLink(lat, lng) {
  if (!lat || !lng) return null;
  return `https://www.google.com/maps?q=${lat},${lng}`;
}
function googleMapsDirections(stops) {
  const filtered = stops.filter(s => s.lat && s.lng);
  if (filtered.length < 2) return null;
  const origin = `${filtered[0].lat},${filtered[0].lng}`;
  const destination = `${filtered[filtered.length - 1].lat},${filtered[filtered.length - 1].lng}`;
  const waypoints = filtered.slice(1, -1).map(s => `${s.lat},${s.lng}`).join("|");
  let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}`;
  if (waypoints) url += `&waypoints=${encodeURIComponent(waypoints)}`;
  return url;
}

export default function RoutesPage() {
  const {  t , lang } = useI18n();
  const { schools, schoolId, setSchoolId, schoolName } = useSchools();
  const [items, setItems] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => { if (schoolId) api.get(`/routes?school_id=${schoolId}`).then(r => setItems(r.data || [])); };
  useEffect(() => {
    if (!schoolId) return;
    load();
    api.get(`/drivers?school_id=${schoolId}`).then(r => setDrivers(r.data || []));
    api.get(`/vehicles?school_id=${schoolId}`).then(r => setVehicles(r.data || []));
  }, [schoolId]);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, stops: [] }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...EMPTY, ...r, stops: r.stops || [] }); setOpen(true); };

  const addStop = () => setForm(f => ({ ...f, stops: [...f.stops, { ...EMPTY_STOP, order: f.stops.length }] }));
  const updateStop = (i, patch) => setForm(f => ({ ...f, stops: f.stops.map((s, idx) => idx === i ? { ...s, ...patch } : s) }));
  const removeStop = (i) => setForm(f => ({ ...f, stops: f.stops.filter((_, idx) => idx !== i) }));

  const pickLocation = (i) => {
    if (!navigator.geolocation) { toast.error(t("geolocation_not_supported")); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => updateStop(i, { lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => toast.error(t("location_error"))
    );
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        school_id: editing ? (editing.school_id || schoolId) : schoolId,
        driver_id: form.driver_id || null,
        vehicle_id: form.vehicle_id || null,
        stops: form.stops.map((s, i) => ({
          ...s,
          lat: s.lat === "" || s.lat === null ? null : Number(s.lat),
          lng: s.lng === "" || s.lng === null ? null : Number(s.lng),
          eta_offset_min: Number(s.eta_offset_min) || 0,
          order: i,
        })),
      };
      if (editing) await api.put(`/routes/${editing.id}`, payload);
      else await api.post("/routes", payload);
      toast.success(t("success")); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const remove = async (r) => {
    try { await api.delete(`/routes/${r.id}`); toast.success(t("success")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const generateToday = async () => {
    try { const r = await api.post(`/trips/generate?school_id=${schoolId}`); toast.success(`${r.data.created} ${t("trips")}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-semibold flex items-center gap-3"><Map className="w-7 h-7 text-primary" /> {t("routes")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("stops")} · Google Maps · {t("start_time")}</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <SchoolSelect schools={schools} value={schoolId} onChange={setSchoolId} testId="routes-school-select" />
          <Button variant="outline" onClick={generateToday} data-testid="generate-trips-btn"><Clock className="w-4 h-4 me-2" />{t("generate_today")}</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button onClick={openNew} data-testid="new-route-btn"><Plus className="w-4 h-4 me-2" />{t("new_route")}</Button></DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editing ? t("edit") : t("new_route")}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>{t("route_name")}</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="route-name" /></div>
                  <div><Label>{t("direction")}</Label>
                    <Select value={form.direction} onValueChange={v => setForm({ ...form, direction: v })}>
                      <SelectTrigger data-testid="route-direction"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="morning">{t("morning")}</SelectItem>
                        <SelectItem value="evening">{t("evening")}</SelectItem>
                        <SelectItem value="both">{t("both")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>{t("start_time")}</Label><Input type="time" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} /></div>
                  <div><Label>{t("driver")}</Label>
                    <Select value={form.driver_id || "none"} onValueChange={v => setForm({ ...form, driver_id: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="route-driver"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {drivers.map(d => <SelectItem key={d.id} value={d.id}>{localFullName(d, lang)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>{t("vehicles")}</Label>
                    <Select value={form.vehicle_id || "none"} onValueChange={v => setForm({ ...form, vehicle_id: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="route-vehicle"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.plate_number} — {v.model}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 flex items-center gap-3"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} id="route-active" /> <Label htmlFor="route-active">{t("active")}</Label></div>
                </div>

                <div className="border-t border-border pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-base">{t("stops")} ({form.stops.length})</Label>
                    <Button variant="outline" size="sm" onClick={addStop} data-testid="add-stop-btn"><Plus className="w-3.5 h-3.5 me-1" />{t("add_stop")}</Button>
                  </div>
                  <div className="space-y-2">
                    {form.stops.map((s, i) => (
                      <div key={i} className="border border-border rounded-lg p-3 space-y-2 bg-muted/30">
                        <div className="flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary grid place-items-center text-xs font-semibold">{i + 1}</span>
                          <Input className="flex-1" value={s.name} onChange={e => updateStop(i, { name: e.target.value })} placeholder={t("stop_name")} data-testid={`stop-name-${i}`} />
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeStop(i)} data-testid={`stop-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                        <Input value={s.address} onChange={e => updateStop(i, { address: e.target.value })} placeholder={t("address") || "Address"} />
                        <div className="grid grid-cols-3 gap-2">
                          <Input type="number" step="0.000001" value={s.lat} onChange={e => updateStop(i, { lat: e.target.value })} placeholder="Lat" data-testid={`stop-lat-${i}`} />
                          <Input type="number" step="0.000001" value={s.lng} onChange={e => updateStop(i, { lng: e.target.value })} placeholder="Lng" />
                          <Input type="number" value={s.eta_offset_min} onChange={e => updateStop(i, { eta_offset_min: e.target.value })} placeholder="+min" />
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <Button variant="outline" size="sm" onClick={() => pickLocation(i)} data-testid={`stop-geo-${i}`}><Navigation className="w-3.5 h-3.5 me-1" />{t("use_my_location")}</Button>
                          {googleMapsLink(s.lat, s.lng) && (
                            <a href={googleMapsLink(s.lat, s.lng)} target="_blank" rel="noreferrer" className="text-xs text-primary underline self-center inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {t("open_map")}</a>
                          )}
                        </div>
                      </div>
                    ))}
                    {form.stops.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">{t("no_data")}</p>}
                  </div>
                </div>

                <Button onClick={save} className="w-full" data-testid="route-save">{t("save")}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {items.map(r => {
          const driver = drivers.find(d => d.id === r.driver_id);
          const vehicle = vehicles.find(v => v.id === r.vehicle_id);
          const mapsLink = googleMapsDirections(r.stops || []);
          return (
            <Card key={r.id} className="p-5 border shadow-none card-hover smooth" data-testid={`route-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold flex items-center gap-2">
                    <Map className="w-4 h-4 text-primary" /> {r.name}
                    {r.active ? <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">{t("active")}</span> : <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">{t("inactive")}</span>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {t(r.direction)} · {r.start_time} · {(r.stops || []).length} {t("stops")}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1" data-testid={`route-school-${r.id}`}>
                    <Building2 className="w-3 h-3" /> {schoolName(r.school_id) || "—"}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {localFullName(driver, lang) || "—"} · {vehicle?.plate_number || "—"}
                  </div>
                </div>
                <RowActions item={r} onEdit={openEdit} onDelete={remove} testIdPrefix="route" />
              </div>
              <ol className="mt-3 space-y-1.5">
                {(r.stops || []).map((s, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <span className="w-5 h-5 rounded-full bg-primary/10 text-primary grid place-items-center text-[10px] font-semibold flex-shrink-0">{i + 1}</span>
                    <span className="flex-1 truncate">{s.name}</span>
                    <span className="text-xs text-muted-foreground font-mono">+{s.eta_offset_min}m</span>
                    {googleMapsLink(s.lat, s.lng) && (
                      <a href={googleMapsLink(s.lat, s.lng)} target="_blank" rel="noreferrer" className="text-primary"><MapPin className="w-3.5 h-3.5" /></a>
                    )}
                  </li>
                ))}
              </ol>
              {mapsLink && (
                <a href={mapsLink} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs text-primary underline" data-testid={`route-view-${r.id}`}>
                  <Navigation className="w-3.5 h-3.5" /> {t("view_route")}
                </a>
              )}
            </Card>
          );
        })}
        {items.length === 0 && <Card className="col-span-full p-12 text-center text-muted-foreground border-dashed">{t("no_data")}</Card>}
      </div>
    </div>
  );
}
